import React, { useState, useCallback, useEffect } from 'react';
import {
  Box,
  Container,
  Grid,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CloseIcon from '@mui/icons-material/Close';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

const API_URL = process.env.REACT_APP_SECUREIFY_API_URL;

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

function Dashboard() {
  const [apiKeys, setApiKeys] = useState([]);
  const [visitors, setVisitors] = useState([]);
  const [visitorStats, setVisitorStats] = useState({});
  const [openNewKeyDialog, setOpenNewKeyDialog] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [selectedVisitor, setSelectedVisitor] = useState(null);
  const [openVisitorDialog, setOpenVisitorDialog] = useState(false);

  const fetchDashboardData = useCallback(async () => {
    try {
      // Fetch user ID from cookies
      const userId = '12345';
      
      // Fetch API keys
      const keysResponse = await fetch(`${API_URL}/api-keys/user/${userId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });
      
      const keysData = await keysResponse.json();
      console.log('Fetched API keys:', keysData);
      setApiKeys(Array.isArray(keysData) ? keysData : []);

      // Fetch visitors
      const visitorsResponse = await fetch(`${API_URL}/dashboard/fingerprints`, {
        headers: {
          'X-API-Key': 'e7740d7e89a56109269736b50c29074740efae6faf61aacc4c71ea7d8d557b7e'
        },
        credentials: 'include'
      });
      const visitorsData = await visitorsResponse.json();
      setVisitors(Array.isArray(visitorsData) ? visitorsData : []);

      // Calculate visitor statistics
      const stats = calculateVisitorStats(visitorsData);
      setVisitorStats(stats);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setVisitors([]);
      setApiKeys([]);
      setVisitorStats({
        totalVisitors: 0,
        uniqueVisitors: 0,
        dailyVisitors: []
      });
    }
  }, []);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  const calculateVisitorStats = (visitors) => {
    const today = new Date();
    const last30Days = [...Array(30)].map((_, i) => {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      return date.toISOString().split('T')[0];
    }).reverse();

    const dailyVisitors = last30Days.map(date => ({
      date,
      count: visitors.length > 0 && visitors.filter(v => 
        v.createdAt.split('T')[0] === date
      ).length
    }));

    return {
      totalVisitors: visitors.length,
      uniqueVisitors: visitors.length > 0 ? new Set(visitors.map(v => v.deviceId)).size : 0,
      dailyVisitors
    };
  };

  const handleCreateApiKey = async () => {
    try {
      const userId = '12345'; // This should come from your auth system
      if (!newKeyName.trim()) {
        console.error('Key name is required');
        return;
      }

      const response = await fetch(`${API_URL}/api-keys`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({ 
          userId: userId,
          name: newKeyName.trim()
        })
      });

      const data = await response.json();
      if (data.error) {
        console.error('Error creating API key:', data.error);
        return;
      }

      // Refresh the dashboard data to get updated API keys
      fetchDashboardData();
      
      setOpenNewKeyDialog(false);
      setNewKeyName('');
    } catch (error) {
      console.error('Error creating API key:', error);
    }
  };

  const handleRevokeApiKey = async (apiKeyId) => {
    try {
      console.log('Revoking API key:', apiKeyId);
      const response = await fetch(`${API_URL}/api-keys/${apiKeyId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });

      if (!response.ok) {
        const data = await response.json();
        console.error('Error revoking API key:', data.error);
        return;
      }

      // Refresh the dashboard data to get updated API keys
      fetchDashboardData();
    } catch (error) {
      console.error('Error revoking API key:', error);
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      console.log('Copied to clipboard:', text);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleViewVisitor = (visitor) => {
    setSelectedVisitor(visitor);
    setOpenVisitorDialog(true);
  };

  const handleCloseVisitorDialog = () => {
    setOpenVisitorDialog(false);
    setSelectedVisitor(null);
  };

  const chartData = {
    labels: visitorStats.dailyVisitors?.map(v => v.date) || [],
    datasets: [
      {
        label: 'Daily Visitors',
        data: visitorStats.dailyVisitors?.map(v => v.count) || [],
        fill: false,
        borderColor: 'rgb(75, 192, 192)',
        tension: 0.1,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: 'Visitors Over Time',
      },
    },
    scales: {
      y: {
        beginAtZero: true,
      },
    },
  };

  return (
    <Box sx={{ flexGrow: 1, pt: 10, pb: 4 }}>
      <Container maxWidth="lg">
        {/* Stats Overview */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Total Visitors
              </Typography>
              <Typography variant="h3">
                {visitorStats.totalVisitors || 0}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Unique Devices
              </Typography>
              <Typography variant="h3">
                {visitorStats.uniqueVisitors || 0}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Active API Keys
              </Typography>
              <Typography variant="h3">
                {apiKeys.length}
              </Typography>
            </Paper>
          </Grid>
        </Grid>

        {/* Visitors Chart */}
        <Paper sx={{ p: 3, mb: 4 }}>
          <Line data={chartData} options={chartOptions} />
        </Paper>

        {/* API Keys Section */}
        <Paper sx={{ p: 3, mb: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
            <Typography variant="h5">API Keys</Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setOpenNewKeyDialog(true)}
            >
              Create New Key
            </Button>
          </Box>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Key</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>Last Used</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {apiKeys.length > 0 && apiKeys.map((apiKey) => (
                  <TableRow key={apiKey._id}>
                    <TableCell>{apiKey.name}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography sx={{ mr: 1 }}>{apiKey.key}</Typography>
                        <Button
                          size="small"
                          onClick={() => copyToClipboard(apiKey.key)}
                        >
                          <ContentCopyIcon fontSize="small" />
                        </Button>
                      </Box>
                    </TableCell>
                    <TableCell>{new Date(apiKey.createdAt).toLocaleDateString()}</TableCell>
                    <TableCell>{apiKey.lastUsedAt ? new Date(apiKey.lastUsedAt).toLocaleDateString() : 'Never'}</TableCell>
                    <TableCell>
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        onClick={() => handleRevokeApiKey(apiKey.key)}
                      >
                        Revoke
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        {/* Recent Visitors */}
        <Paper sx={{ p: 3 }}>
          <Typography variant="h5" sx={{ mb: 3 }}>
            Recent Visitors
          </Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Device ID</TableCell>
                  <TableCell>First Seen</TableCell>
                  <TableCell>Last Seen</TableCell>
                  <TableCell>Visits</TableCell>
                  <TableCell>Details</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {visitors.length > 0 && visitors.slice(0, 10).map((visitor) => (
                  <TableRow key={visitor._id}>
                    <TableCell>{visitor.deviceId}</TableCell>
                    <TableCell>{new Date(visitor.createdAt).toLocaleDateString()}</TableCell>
                    <TableCell>{new Date(visitor.lastSeenAt).toLocaleDateString()}</TableCell>
                    <TableCell>{visitor.visits}</TableCell>
                    <TableCell>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => handleViewVisitor(visitor)}
                      >
                        View
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Container>

      {/* New API Key Dialog */}
      <Dialog open={openNewKeyDialog} onClose={() => setOpenNewKeyDialog(false)}>
        <DialogTitle>Create New API Key</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Key Name"
            fullWidth
            variant="outlined"
            value={newKeyName}
            onChange={(e) => setNewKeyName(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenNewKeyDialog(false)}>Cancel</Button>
          <Button onClick={handleCreateApiKey} variant="contained">
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* Visitor Details Dialog */}
      <Dialog
        open={openVisitorDialog}
        onClose={handleCloseVisitorDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Device Details
          <IconButton
            aria-label="close"
            onClick={handleCloseVisitorDialog}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {selectedVisitor && (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Device Information
                </Typography>
                <Typography variant="body1">
                  Device ID: {selectedVisitor.deviceId}
                </Typography>
                <Typography variant="body1">
                  First Seen: {new Date(selectedVisitor.createdAt).toLocaleString()}
                </Typography>
                <Typography variant="body1">
                  Last Seen: {new Date(selectedVisitor.lastSeenAt).toLocaleString()}
                </Typography>
                <Typography variant="body1">
                  Total Visits: {selectedVisitor.visits}
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Browser Information
                </Typography>
                <Typography variant="body1">
                  User Agent: {selectedVisitor.parameters?.browser?.userAgent || 'Unknown'}
                </Typography>
                <Typography variant="body1">
                  Language: {selectedVisitor.parameters?.browser?.language || 'Unknown'}
                </Typography>
                <Typography variant="body1">
                  Platform: {selectedVisitor.parameters?.browser?.platform || 'Unknown'}
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Screen Information
                </Typography>
                <Typography variant="body1">
                  Resolution: {selectedVisitor.parameters?.screen?.width || 0}x{selectedVisitor.parameters?.screen?.height || 0}
                </Typography>
                <Typography variant="body1">
                  Color Depth: {selectedVisitor.parameters?.screen?.colorDepth || 0}-bit
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Location Information
                </Typography>
                <Typography variant="body1">
                  Timezone: {selectedVisitor.parameters?.timezone?.timezone || 'Unknown'}
                </Typography>
                <Typography variant="body1">
                  UTC Offset: {selectedVisitor.parameters?.timezone?.offset || 0} minutes
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  All Parameters
                </Typography>
                <Box sx={{ 
                  bgcolor: 'background.default',
                  p: 2,
                  borderRadius: 1,
                  maxHeight: 200,
                  overflow: 'auto'
                }}>
                  <pre style={{ margin: 0 }}>
                    {JSON.stringify(selectedVisitor.parameters, null, 2)}
                  </pre>
                </Box>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseVisitorDialog}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Dashboard;
